/**
 * Drives the first-run tour for Ben (and any review coach).
 *
 * Owns the step index, TTS playback per step, voice listening for
 * advance phrases, route navigation when a step has a target route,
 * and the final intent classifier for the voice-routed "what would
 * you like to work on first?".
 *
 * Composed in `FirstRunTour` overlay component (W5). The hook is
 * deliberately UI-free — it returns state + callbacks; the overlay
 * decides how to render.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTextToSpeech } from '../../hooks/useTextToSpeech';
import { useVoiceToText } from '../voiceInput';
import { useCoachReviewMode } from '../../hooks/useCoachReviewMode';
import {
  FIRST_RUN_STEPS,
  fillSayCopy,
  type FirstRunStep,
  INTENT_TO_PATH,
  type IntentResult,
} from './firstRunScript';
import { callAIJson } from '@4cm/core/ai';

export interface FirstRunTourState {
  /** Currently-active step. */
  step: FirstRunStep;
  /** 1-based step index for display ("Step 2 of 5"). */
  stepIndex: number;
  totalSteps: number;
  /** True while the step's TTS audio is playing. */
  speaking: boolean;
  /** True when the mic is armed + listening for advance phrases. */
  listening: boolean;
  /** True after the final step's intent has been classified and we're
   *  navigating away. UI should freeze the tour at this point. */
  routing: boolean;
  /** False until the coach taps "Start". That tap is the user gesture the
   *  browser requires before it will play Brain's first line — without it
   *  the opening utterance is autoplay-blocked (silent). Nothing is spoken
   *  and the mic isn't armed until this flips true. */
  started: boolean;
  /** Begin the tour: flips `started`, which (in the same gesture) lets the
   *  first step speak + arms the mic. Wired to the Start button on step 1. */
  start: () => void;
  /** Advance to the next step (or, on final step, classify intent). */
  next: () => void;
  /** Skip the entire tour — marks first_run_completed_at + navigates
   *  to /sessions. */
  skip: () => void;
  /** Toggle the voice mic for the open prompt. Tour also auto-arms it
   *  on step entry. */
  startListening: () => void;
  stopListening: () => void;
}

export function useFirstRunTour(): FirstRunTourState {
  const navigate = useNavigate();
  const { coachName, loading: reviewLoading, markFirstRunCompleted } = useCoachReviewMode();
  const tts = useTextToSpeech({ provider: 'eleven', voiceId: 'george' });
  // Persist step index across remounts. The tour's route navigation
  // can cause the AppShell-level gate to briefly drop, unmounting the
  // tour — without persistence, the index resets to 0 and the coach
  // sees Step 1 again. sessionStorage survives the unmount + clears
  // on tab close (so future browser sessions start fresh).
  const [stepIdx, setStepIdx] = useState<number>(() => {
    if (typeof window === 'undefined') return 0;
    const raw = window.sessionStorage.getItem('firstRunStepIdx');
    const parsed = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(parsed) && parsed >= 0 && parsed < FIRST_RUN_STEPS.length ? parsed : 0;
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('firstRunStepIdx', String(stepIdx));
    }
  }, [stepIdx]);
  const [routing, setRouting] = useState(false);
  // Gates the first utterance behind a user gesture (the Start tap) so the
  // browser's autoplay policy doesn't silently block Brain's opening line.
  // Derived from the persisted step so a remount MID-tour (stepIdx survives in
  // sessionStorage; component state doesn't) doesn't reset us to "not started"
  // — which would strand step 2+ with no Start button and audio still gated.
  const [started, setStarted] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const raw = window.sessionStorage.getItem('firstRunStepIdx');
    const parsed = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(parsed) && parsed > 0;
  });
  const start = useCallback(() => setStarted(true), []);
  const lastSpokenStepRef = useRef<number>(-1);

  const step = FIRST_RUN_STEPS[stepIdx];
  const totalSteps = FIRST_RUN_STEPS.length;

  // Speak the step's brainSay when we enter the step. Don't re-speak
  // on every render of the same step.
  //
  // Hold until the review-mode hook has hydrated: coachName loads async
  // from the users row, and the hook sets it + flips `loading` in the same
  // batch. Speaking before that would voice the "{coachName}" → "Coach"
  // fallback while the on-screen bubble (re-rendered post-hydration) shows
  // the real name — so the audio said "Hi Coach" while the text said
  // "Hi Ben". Gating on !reviewLoading keeps the first utterance personal.
  useEffect(() => {
    // Wait for the Start tap (autoplay gate) AND for review-mode hydration
    // (so coachName is resolved — see below). Once started, the first step
    // speaks within the tap's activation window; later steps speak on entry.
    if (!started || reviewLoading) return;
    if (lastSpokenStepRef.current === stepIdx) return;
    lastSpokenStepRef.current = stepIdx;
    const copy = fillSayCopy(step.brainSay, coachName);
    tts.speakSentenceChunked(copy);
  }, [stepIdx, step.brainSay, coachName, reviewLoading, started, tts]);

  // Navigate to the step's target route if one is set. Fires when the
  // step changes — the spotlight overlay handles selector mounting.
  useEffect(() => {
    if (step.target?.route) {
      navigate(step.target.route);
    }
  }, [stepIdx, step.target?.route, navigate]);

  const advance = useCallback(() => {
    if (stepIdx >= totalSteps - 1) return;
    setStepIdx((i) => Math.min(totalSteps - 1, i + 1));
  }, [stepIdx, totalSteps]);

  const exitToSessions = useCallback(async () => {
    setRouting(true);
    tts.stop();
    if (typeof window !== 'undefined') window.sessionStorage.removeItem('firstRunStepIdx');
    await markFirstRunCompleted();
    navigate('/sessions');
  }, [markFirstRunCompleted, navigate, tts]);

  // Voice handler: matches the step's expectedReplies for steps 1–4,
  // classifies intent + navigates for the final step (step 5).
  const handleTranscript = useCallback((text: string) => {
    const clean = text.trim().toLowerCase();
    if (!clean) return;
    if (step.isFinal) {
      // Final step — classify intent then route.
      setRouting(true);
      void (async () => {
        let route: IntentResult['route'] = 'open';
        try {
          const result = await callAIJson<IntentResult>({
            surface: 'brain-suggestions',
            system:
              'Classify a coach utterance into one of: sessions | drills | open. Return STRICT JSON of shape { "route": "..." }.',
            userContent: `Coach said: "${text}"\n\nReturn JSON.`,
            maxTokens: 60,
          });
          if (result?.route && ['sessions', 'drills', 'open'].includes(result.route)) {
            route = result.route;
          }
        } catch {
          // Network / parse fail — default to sessions (safest).
        }
        await markFirstRunCompleted();
        navigate(INTENT_TO_PATH[route]);
      })();
      return;
    }
    // Non-final step — match expectedReplies.
    const accepted = step.expectedReplies ?? [];
    if (accepted.some((p) => clean.includes(p.toLowerCase()))) {
      advance();
    }
  }, [step, advance, markFirstRunCompleted, navigate]);

  const voice = useVoiceToText({ onTranscript: handleTranscript });

  // Auto-arm the mic on step entry (after Start). Coach is voice-first; we
  // don't want them tapping anything. Held until `started` so we don't ask
  // for the mic before the coach has engaged the tour.
  useEffect(() => {
    if (routing || !started) return;
    // Defer slightly so the TTS gets a clear start without the mic
    // catching the speaker's audio bleed (esp. on built-in laptop mics).
    const timer = setTimeout(() => {
      void voice.start();
    }, 800);
    return () => {
      clearTimeout(timer);
      voice.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIdx, started]);

  const next = useCallback(() => {
    if (step.isFinal) {
      // Final step with no voice — exit to sessions.
      void exitToSessions();
      return;
    }
    advance();
  }, [step.isFinal, advance, exitToSessions]);

  return useMemo<FirstRunTourState>(() => ({
    step,
    stepIndex: stepIdx + 1,
    totalSteps,
    speaking: tts.isSpeaking,
    listening: voice.listening,
    routing,
    started,
    start,
    next,
    skip: () => void exitToSessions(),
    startListening: () => void voice.start(),
    stopListening: voice.stop,
  }), [step, stepIdx, totalSteps, tts.isSpeaking, voice.listening, voice.start, voice.stop, routing, started, start, next, exitToSessions]);
}
